import { useEffect, useState } from 'react';
import { getConsent, setConsent, acceptAll, rejectAll } from '@/lib/consent';
import { isQAMode } from '@/lib/tp13Config';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(false);

  useEffect(() => {
    // Mode QA : ne rien afficher et ne rien charger
    if (isQAMode()) {
      return;
    }

    const consent = getConsent();
    if (!consent) {
      // Pas de consentement ou consentement expiré : afficher le bandeau
      setShowBanner(true);
    } else {
      // Charger les scripts si consentement valide
      loadScripts(consent.analytics, consent.functional);
    }

    // Écouter l'event pour ouvrir les settings
    const handleOpenSettings = () => {
      const current = getConsent();
      if (current) {
        setAnalytics(current.analytics);
        setFunctional(current.functional);
      }
      setShowSettings(true);
    };

    window.addEventListener('tp13:open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('tp13:open-cookie-settings', handleOpenSettings);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setShowBanner(false);
    loadScripts(true, true);
  };

  const handleRejectAll = () => {
    rejectAll();
    setShowBanner(false);
    // Ne charger aucun script non-essentiel
  };

  const handleSaveSettings = () => {
    setConsent({
      necessary: true,
      analytics,
      functional,
      ts: new Date().toISOString(),
    });
    setShowSettings(false);
    setShowBanner(false);
    loadScripts(analytics, functional);
  };

  const loadScripts = (analyticsEnabled: boolean, functionalEnabled: boolean) => {
    // Mode QA : ne jamais charger de scripts
    if (isQAMode()) {
      return;
    }

    // GA4 : uniquement si consentement analytics
    if (analyticsEnabled && import.meta.env.VITE_GA4_ID) {
      const ga4Id = import.meta.env.VITE_GA4_ID;
      if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${ga4Id}"]`)) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
        document.head.appendChild(script);

        const inlineScript = document.createElement('script');
        inlineScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${ga4Id}', {
            'anonymize_ip': true,
            'cookie_flags': 'SameSite=None;Secure'
          });
        `;
        document.head.appendChild(inlineScript);
      }
    }

    // Crisp : uniquement si consentement functional
    if (functionalEnabled && import.meta.env.VITE_CRISP_ID) {
      const crispId = import.meta.env.VITE_CRISP_ID;
      if (!window.$crisp) {
        window.$crisp = [];
        window.CRISP_WEBSITE_ID = crispId;
        const script = document.createElement('script');
        script.src = 'https://client.crisp.chat/l.js';
        script.async = true;
        document.head.appendChild(script);
      }
    }
  };

  // Mode QA : ne rien afficher
  if (isQAMode()) {
    return null;
  }

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Bannière */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-[#141414] border-t border-gray-800 shadow-2xl">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg mb-2 text-white">🍪 Gestion des cookies</h3>
                <p className="text-sm text-gray-300 break-words">
                  Nous utilisons des cookies pour améliorer votre expérience. Les cookies nécessaires sont toujours actifs. 
                  Vous pouvez personnaliser vos préférences ou tout accepter. Le refus est aussi simple que l'acceptation.
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Votre choix sera conservé 6 mois maximum (recommandation CNIL).
                </p>
              </div>
              <div className="flex flex-wrap gap-3 flex-shrink-0">
                <button
                  onClick={handleRejectAll}
                  className="px-6 py-2.5 border border-gray-700 hover:border-[#DC143C] text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  aria-label="Refuser tous les cookies non essentiels"
                >
                  Refuser
                </button>
                <button
                  onClick={() => {
                    const current = getConsent();
                    if (current) {
                      setAnalytics(current.analytics);
                      setFunctional(current.functional);
                    }
                    setShowSettings(true);
                  }}
                  className="px-6 py-2.5 border border-gray-700 hover:border-[#DC143C] text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  aria-label="Personnaliser mes préférences de cookies"
                >
                  Personnaliser
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 bg-[#DC143C] hover:bg-[#B01030] text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  aria-label="Accepter tous les cookies"
                >
                  Tout accepter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Settings */}
      {showSettings && (
        <>
          <div
            className="fixed inset-0 bg-black/70 z-[9998]"
            onClick={() => setShowSettings(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="bg-[#141414] border border-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-800">
                <h2 className="text-2xl font-bold text-white">Préférences de cookies</h2>
                <p className="text-sm text-gray-400 mt-2">
                  Choisissez les catégories de cookies que vous souhaitez autoriser. Votre choix sera conservé 6 mois.
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Nécessaires */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1 text-white flex items-center gap-2">
                      <i className="ri-shield-check-line text-green-400"></i>
                      Cookies nécessaires
                    </h3>
                    <p className="text-sm text-gray-300 break-words">
                      Indispensables au fonctionnement du site (authentification, sécurité, mémorisation de vos préférences de consentement).
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Exemptés de consentement par la CNIL
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="w-5 h-5 rounded border-gray-700 bg-[#0a0a0a] cursor-not-allowed"
                      aria-label="Cookies nécessaires toujours actifs"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">Toujours actif</span>
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1 text-white flex items-center gap-2">
                      <i className="ri-bar-chart-line text-blue-400"></i>
                      Cookies analytiques
                    </h3>
                    <p className="text-sm text-gray-300 break-words">
                      Nous aident à comprendre comment vous utilisez le site pour l'améliorer (Google Analytics 4, IP anonymisée).
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Transfert hors UE (États-Unis) - Clauses Contractuelles Types
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-700 bg-[#0a0a0a] cursor-pointer accent-[#DC143C]"
                      aria-label="Activer les cookies analytiques"
                    />
                  </label>
                </div>

                {/* Fonctionnels */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1 text-white flex items-center gap-2">
                      <i className="ri-customer-service-line text-purple-400"></i>
                      Cookies fonctionnels
                    </h3>
                    <p className="text-sm text-gray-300 break-words">
                      Améliorent votre expérience (chat support Crisp, historique des conversations).
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Hébergement UE (France) - Pas de transfert hors UE
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={functional}
                      onChange={(e) => setFunctional(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-700 bg-[#0a0a0a] cursor-pointer accent-[#DC143C]"
                      aria-label="Activer les cookies fonctionnels"
                    />
                  </label>
                </div>
              </div>

              <div className="p-6 border-t border-gray-800">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-300">
                    <i className="ri-information-line mr-2"></i>
                    Vous pouvez modifier vos préférences à tout moment depuis la page{' '}
                    <a href="/cookies" className="text-[#DC143C] hover:underline">Politique cookies</a>.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-6 py-2.5 border border-gray-700 hover:border-[#DC143C] text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    className="px-6 py-2.5 bg-[#DC143C] hover:bg-[#B01030] text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Enregistrer mes préférences
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Déclaration TypeScript pour Crisp
declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
  }
}